import { type ElementType, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Activity, ArrowUp, BarChart3, Moon, Sun, Table2 } from 'lucide-react';
import * as echarts from 'echarts';
import type { EChartsType } from 'echarts';

import { AttendanceChart } from './components/AttendanceChart';
import AchievementsPage from './components/AchievementsPage';
import { ContactDensityChart } from './components/ContactDensityChart';
import { FragsDistanceChart } from './components/FragsDistanceChart';
import { MapsChart } from './components/MapsChart';
import { PlayerStatsTable } from './components/PlayerStatsTable';
import { GenericStatsTable, type GenericColumn, type GenericRow } from './components/GenericStatsTable';
import { SquadStatsTable } from './components/SquadStatsTable';
import { SquadDetailPage } from './components/SquadDetailPage';
import { PlayerDetailPage } from './components/PlayerDetailPage';
import { SideStatsPage } from './components/SideStatsPage';
import {
  HeaderNavigationMenu,
  MANUAL_CORRECTION_LINKS,
  STATS_LINKS,
  type HeaderLink,
} from './components/HeaderNavigationMenu';
import { ManualCorrectionsPage } from './components/manualCorrections/ManualCorrectionsPage';
import { ManualCorrectionRegistry } from './components/manualCorrections/ManualCorrectionRegistry';
import { ManualCorrectionReceiptPage } from './components/manualCorrections/ManualCorrectionReceiptPage';
import { ModerationPage, type ModerationRoute } from './components/moderation/ModerationPage';
import { apiFetch } from './lib/api';

const ksColumns: GenericColumn[] = [
  { key: 'rank', label: 'Рейтинг', type: 'number' },
  { key: 'player', label: 'Игрок', type: 'string' },
  { key: 'games', label: 'Кол-во игр', type: 'number' },
  { key: 'wins', label: 'Победы', type: 'number' },
  { key: 'losses', label: 'Поражения', type: 'number' },
  { key: 'taskWins', label: 'Поб. по задачам', type: 'number' },
  { key: 'lossWins', label: 'Поб. по потерям', type: 'number' },
  { key: 'taskLoss', label: 'Пр. по задачам', type: 'number' },
  { key: 'lossLoss', label: 'Пр. по потерям', type: 'number' },
  { key: 'efficiency', label: 'КПД', type: 'number' },
];

type KsRow = {
  rank: number;
  player: string;
  games: number;
  wins: number;
  losses: number;
  taskWins: number;
  lossWins: number;
  taskLoss: number;
  lossLoss: number;
  efficiency: number;
};

const mtsgColumns: GenericColumn[] = [
  { key: 'rank', label: 'Рейтинг', type: 'number' },
  { key: 'callsign', label: 'Позывной', type: 'string' },
  { key: 'attendance', label: 'Посещаемость', type: 'number' },
  { key: 'frags', label: 'Фраги', type: 'number' },
  { key: 'tk', label: 'ТК', type: 'number' },
  { key: 'tkDeaths', label: 'Смерти от ТК', type: 'number' },
  { key: 'survival', label: 'Выживаемость', type: 'number' },
  { key: 'medic', label: 'Медицина', type: 'number' },
  { key: 'ego', label: 'ЧСВ', type: 'number' },
  { key: 'efficiency', label: 'КПД', type: 'number' },
];

const mtsgRows: GenericRow[] = [
  { rank: 1, callsign: 'SADBOY', attendance: 2, frags: 17, tk: 0, tkDeaths: 1, survival: 0.004, medic: 8.5, ego: 8.514, efficiency: 8.514 },
  { rank: 2, callsign: 'Salat', attendance: 2, frags: 11, tk: 0, tkDeaths: 1, survival: 0.0, medic: 5.5, ego: 5.509, efficiency: 5.509 },
  { rank: 3, callsign: 'AimFire', attendance: 2, frags: 10, tk: 0, tkDeaths: 0, survival: 0.0, medic: 5.0, ego: 5.0, efficiency: 5.0 },
  { rank: 4, callsign: 'actibo', attendance: 1, frags: 5, tk: 0, tkDeaths: 0, survival: 0.0, medic: 5.0, ego: 5.0, efficiency: 5.0 },
  { rank: 5, callsign: 'Priest', attendance: 2, frags: 9, tk: 0, tkDeaths: 1, survival: 0.0, medic: 4.5, ego: 4.509, efficiency: 4.509 },
];

const mtsgReplayInfo = 'Последний обработанный реплей: mtsg_some_misson';

function StatCard({
  title,
  value,
  icon: Icon,
  isLoading,
}: {
  title: string;
  value: string;
  icon: ElementType;
  isLoading?: boolean;
}) {
  return (
    <motion.div
      className="card p-4 flex items-center gap-3 hover:-translate-y-1 hover:ring-2 hover:ring-accent/60 hover:shadow-lg"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
    >
      <div className="p-2 rounded-md bg-primary/10 text-primary">
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <p className="text-sm text-muted-foreground">{title}</p>
        {isLoading ? (
          <motion.div
            className="mt-1 h-6 w-24 rounded bg-muted"
            animate={{ opacity: [0.4, 1, 0.4] }}
            transition={{ duration: 1.2, repeat: Infinity }}
          />
        ) : (
          <p className="text-xl font-semibold">{value}</p>
        )}
      </div>
    </motion.div>
  );
}

function App(): JSX.Element {
  type Theme = 'light' | 'dark';
  type Page =
    | 'dashboard'
    | 'playerStats'
    | 'playerDetail'
    | 'squadStats'
    | 'squadDetail'
    | 'sideStats'
    | 'achievements'
    | 'heatmaps'
    | 'infantry'
    | 'vehicles'
    | 'artillery'
    | 'ks'
    | 'mtsg'
    | 'archive'
    | 'manualCorrections'
    | 'manualCorrectionRegistry'
    | 'manualCorrectionReceipt'
    | 'moderation';

  const themeStorageKey = 'webstat-theme';

  const getInitialTheme = (): Theme => {
    if (typeof window === 'undefined') {
      return 'light';
    }

    const stored = window.localStorage.getItem(themeStorageKey);
    if (stored === 'light' || stored === 'dark') {
      return stored;
    }

    const prefersDark = window.matchMedia?.('(prefers-color-scheme: dark)').matches;
    return prefersDark ? 'dark' : 'light';
  };

  const [theme, setTheme] = useState<Theme>(getInitialTheme);
  const resolveSquadFromPath = (): string | null => {
    if (typeof window === 'undefined') return null;
    const match = window.location.pathname.match(/^\/squad-stats\/(.+)$/);
    return match ? decodeURIComponent(match[1]) : null;
  };

  const resolvePlayerFromPath = (): string | null => {
    if (typeof window === 'undefined') return null;
    const match = window.location.pathname.match(/^\/player\/(.+)$/);
    return match ? decodeURIComponent(match[1]) : null;
  };

  const resolveSideServerFromPath = (): string | null => {
    if (typeof window === 'undefined') return null;
    const match = window.location.pathname.match(/^\/srv_stat\/([^/]+)$/);
    return match ? decodeURIComponent(match[1]) : null;
  };

  const resolveManualCorrectionIdFromPath = (): string | null => {
    if (typeof window === 'undefined') return null;
    if (/^\/manual-corrections\/registry\/?$/.test(window.location.pathname)) return null;
    const match = window.location.pathname.match(/^\/manual-corrections\/([^/]+)\/?$/);
    return match ? decodeURIComponent(match[1]) : null;
  };

  const resolveModerationRoute = (pathname: string): ModerationRoute => {
    if (pathname === '/moderation/login' || pathname === '/moderation/login/') {
      return { kind: 'login' };
    }
    if (
      pathname === '/moderation/manual-corrections' ||
      pathname === '/moderation/manual-corrections/'
    ) {
      return { kind: 'queue' };
    }
    const match = pathname.match(/^\/moderation\/manual-corrections\/([^/]+)\/?$/);
    if (!match) return { kind: 'invalid' };
    try {
      return { kind: 'detail', publicId: decodeURIComponent(match[1]) };
    } catch {
      return { kind: 'invalid' };
    }
  };

  const resolvePage = (): Page => {
    if (typeof window === 'undefined') return 'dashboard';
    if (window.location.pathname.match(/^\/player\//)) return 'playerDetail';
    if (window.location.pathname.startsWith('/player-stats')) return 'playerStats';
    if (window.location.pathname.match(/^\/squad-stats\//)) return 'squadDetail';
    if (window.location.pathname.startsWith('/squad-stats')) return 'squadStats';
    if (window.location.pathname.startsWith('/srv_stat')) return 'sideStats';
    if (window.location.pathname.startsWith('/achievements')) return 'achievements';
    if (window.location.pathname.startsWith('/heatmaps')) return 'heatmaps';
    if (window.location.pathname.startsWith('/infantry')) return 'infantry';
    if (window.location.pathname.startsWith('/vehicles')) return 'vehicles';
    if (window.location.pathname.startsWith('/artillery')) return 'artillery';
    if (window.location.pathname.startsWith('/ks')) return 'ks';
    if (window.location.pathname.startsWith('/mtsg')) return 'mtsg';
    if (window.location.pathname.startsWith('/archive')) return 'archive';
    if (window.location.pathname.startsWith('/moderation')) return 'moderation';
    if (/^\/manual-corrections\/registry\/?$/.test(window.location.pathname)) {
      return 'manualCorrectionRegistry';
    }
    if (resolveManualCorrectionIdFromPath()) return 'manualCorrectionReceipt';
    if (window.location.pathname.startsWith('/manual-corrections')) return 'manualCorrections';
    return 'dashboard';
  };
  const [page, setPage] = useState<Page>(resolvePage);
  const [selectedSquad, setSelectedSquad] = useState<string | null>(resolveSquadFromPath);
  const [selectedPlayer, setSelectedPlayer] = useState<string | null>(resolvePlayerFromPath);
  const [selectedServer, setSelectedServer] = useState<string | null>(resolveSideServerFromPath);
  const [manualCorrectionId, setManualCorrectionId] = useState<string | null>(resolveManualCorrectionIdFromPath);
  const [locationPathname, setLocationPathname] = useState(() => window.location.pathname);
  const [locationSearch, setLocationSearch] = useState(() => window.location.search);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const mobileMenuRef = useRef<HTMLDivElement | null>(null);
  const isManualCorrectionPage =
    page === 'manualCorrections' ||
    page === 'manualCorrectionRegistry' ||
    page === 'manualCorrectionReceipt' ||
    page === 'moderation';
  const { data: summaryCards, isLoading: isSummaryLoading } = useQuery({
    queryKey: ['summary-cards'],
    queryFn: () =>
      apiFetch<{
        enemies_killed: number;
        friendly_fire_deaths: number;
        survivors: number;
      }>('/summary-cards/'),
    enabled: !isManualCorrectionPage,
  });
  const { data: updateDate } = useQuery({
    queryKey: ['update-date'],
    queryFn: () => apiFetch<{ updated_at: string | null }>('/update-date/'),
    enabled: !isManualCorrectionPage,
  });
  const { data: ksStatsResponse } = useQuery({
    queryKey: ['ks-stats'],
    queryFn: () => apiFetch<{ items: KsRow[] }>('/ks-stats/'),
    enabled: !isManualCorrectionPage,
  });
  const kpis = useMemo(() => {
    const formatter = new Intl.NumberFormat('ru-RU');
    const formatValue = (value?: number) => (value === undefined ? '—' : formatter.format(value));

    return [
      { title: 'Виртуальных врагов убито', value: formatValue(summaryCards?.enemies_killed), icon: Activity },
      { title: 'Погибших от дружественного огня', value: formatValue(summaryCards?.friendly_fire_deaths), icon: BarChart3 },
      { title: 'Бойцов выжило', value: formatValue(summaryCards?.survivors), icon: Table2 },
    ];
  }, [summaryCards]);
  const formattedUpdateDate = updateDate?.updated_at ?? '—';

  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle('dark', theme === 'dark');
    window.localStorage.setItem(themeStorageKey, theme);
  }, [theme]);

  useEffect(() => {
    if (!mobileMenuOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (!mobileMenuRef.current) return;
      if (!mobileMenuRef.current.contains(event.target as Node)) {
        setMobileMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [mobileMenuOpen]);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 200);
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [theme]);

  useEffect(() => {
    const handlePopState = () => {
      setPage(resolvePage());
      setSelectedSquad(resolveSquadFromPath());
      setSelectedPlayer(resolvePlayerFromPath());
      setSelectedServer(resolveSideServerFromPath());
      setManualCorrectionId(resolveManualCorrectionIdFromPath());
      setLocationPathname(window.location.pathname);
      setLocationSearch(window.location.search);
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const navigateTo = useCallback((path: string) => {
    const targetUrl = new URL(path, window.location.origin);
    const pathname = targetUrl.pathname;
    let targetPage: Page = 'dashboard';
    if (pathname.startsWith('/player-stats')) {
      targetPage = 'playerStats';
      setSelectedPlayer(null);
    } else if (pathname.match(/^\/player\//)) {
      targetPage = 'playerDetail';
      const [, playerSlug] = pathname.split('/player/');
      setSelectedPlayer(playerSlug ? decodeURIComponent(playerSlug) : null);
    } else if (pathname.match(/^\/squad-stats\//)) {
      targetPage = 'squadDetail';
      const [, squadSlug] = pathname.split('/squad-stats/');
      setSelectedSquad(squadSlug ? decodeURIComponent(squadSlug) : null);
    } else if (pathname.startsWith('/squad-stats')) {
      targetPage = 'squadStats';
      setSelectedSquad(null);
    } else if (pathname.match(/^\/srv_stat\/[^/]+$/)) {
      targetPage = 'sideStats';
      const [, serverSlug] = pathname.split('/srv_stat/');
      setSelectedServer(serverSlug || null);
    } else if (pathname.startsWith('/srv_stat')) {
      targetPage = 'sideStats';
      setSelectedServer(null);
    } else if (pathname.startsWith('/achievements')) {
      targetPage = 'achievements';
    } else if (pathname.startsWith('/heatmaps')) {
      targetPage = 'heatmaps';
    } else if (pathname.startsWith('/infantry')) {
      targetPage = 'infantry';
      setSelectedPlayer(null);
    } else if (pathname.startsWith('/vehicles')) {
      targetPage = 'vehicles';
      setSelectedPlayer(null);
    } else if (pathname.startsWith('/artillery')) {
      targetPage = 'artillery';
      setSelectedPlayer(null);
    } else if (pathname.startsWith('/ks')) {
      targetPage = 'ks';
      setSelectedPlayer(null);
    } else if (pathname.startsWith('/mtsg')) {
      targetPage = 'mtsg';
      setSelectedPlayer(null);
    } else if (pathname.startsWith('/archive')) {
      targetPage = 'archive';
    } else if (pathname.startsWith('/moderation')) {
      targetPage = 'moderation';
    } else if (/^\/manual-corrections\/registry\/?$/.test(pathname)) {
      targetPage = 'manualCorrectionRegistry';
      setManualCorrectionId(null);
    } else if (pathname.match(/^\/manual-corrections\/[^/]+\/?$/)) {
      targetPage = 'manualCorrectionReceipt';
      const [, publicId] = pathname.split('/manual-corrections/');
      setManualCorrectionId(publicId ? decodeURIComponent(publicId.replace(/\/$/, '')) : null);
    } else if (pathname.startsWith('/manual-corrections')) {
      targetPage = 'manualCorrections';
      setManualCorrectionId(null);
    }

    setPage(targetPage);
    setLocationPathname(pathname);
    setLocationSearch(targetUrl.search);
    window.history.pushState({ page: targetPage }, '', path);

    if (targetUrl.hash) {
      const hash = targetUrl.hash.slice(1);
      if (hash) {
        requestAnimationFrame(() => {
          const anchor = document.getElementById(hash);
          anchor?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      }
    }
  }, []);

  const renderTablePage = (
    title: string,
    subtitle: string,
    columns: GenericColumn[],
    rows: GenericRow[],
    linkKey: string = 'callsign',
    efficiencyKey = 'efficiency',
    infoContent?: Record<string, { title: string; description: ReactNode }>
  ) => (
    <main className="mx-auto w-full max-w-7xl space-y-6 px-4 py-6 sm:px-6">
      <GenericStatsTable
        title={title}
        subtitle={subtitle}
        columns={columns}
        rows={rows}
        linkKey={linkKey}
        efficiencyKey={efficiencyKey}
        infoContent={infoContent}
        onLinkClick={(nickname) => navigateTo(`/player/${encodeURIComponent(nickname)}`)}
      />
    </main>
  );

  return (
    <div className="min-h-screen bg-background text-foreground transition-colors">
      <header className="relative z-50 border-b border-border bg-card/95 shadow-sm backdrop-blur">
        <div className="mx-auto grid w-full max-w-7xl grid-cols-[auto,1fr,auto] items-center gap-4 px-4 py-3 sm:px-6">
          <div className="flex items-center gap-3">
            <div ref={mobileMenuRef} className="relative lg:hidden">
              <button
                type="button"
                onClick={() => setMobileMenuOpen((prev) => !prev)}
                className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-border bg-card text-foreground transition hover:border-primary hover:text-primary"
                aria-label="Открыть меню"
                aria-expanded={mobileMenuOpen}
              >
                ☰
              </button>
              <HeaderNavigationMenu
                open={mobileMenuOpen}
                sections={[
                  { label: 'Статистика', links: STATS_LINKS },
                  { label: 'Учёт фрагов', links: MANUAL_CORRECTION_LINKS },
                ]}
                onClose={() => setMobileMenuOpen(false)}
                onNavigate={navigateTo}
              />
            </div>
            <a
              href="/"
              onClick={(event) => {
                event.preventDefault();
                navigateTo('/');
              }}
              className="flex items-center gap-3 min-w-0 transition hover:opacity-90"
            >
              <img src="/logo.svg" alt="Логотип Подпольной статистики" className="h-10 w-auto" />
              <p className="text-lg font-semibold text-foreground sm:text-xl">Подпольная статистика</p>
            </a>
          </div>

          <div className="flex min-w-0 flex-1 items-center gap-3">
            <NavigationSparkline theme={theme} statsLinks={STATS_LINKS} onNavigate={navigateTo} />
          </div>

          <div className="flex items-center justify-end gap-3">
            <a
              className="hidden rounded-md border border-primary/40 bg-primary/15 px-3 py-2 text-sm font-semibold text-foreground transition hover:bg-primary hover:text-primary-foreground sm:inline-flex"
              href="/moderation/login"
              onClick={(event) => {
                event.preventDefault();
                navigateTo('/moderation/login');
              }}
            >
              Вход
            </a>
            <a
              className="hidden rounded-md border border-primary/40 bg-primary/15 px-3 py-2 text-sm font-semibold text-foreground transition hover:bg-primary hover:text-primary-foreground sm:inline-flex"
              href="https://tsgames.ru/"
              target="_blank"
              rel="noreferrer"
            >
              Сайт TSG
            </a>
            <div className="hidden text-right text-xs text-muted-foreground sm:block">
              <p className="font-semibold text-foreground">Дата обновления</p>
              {updateDate?.updated_at ? (
                <p>{formattedUpdateDate}</p>
              ) : (
                <motion.p
                  animate={{ opacity: [0.4, 1, 0.4] }}
                  transition={{ duration: 1.2, repeat: Infinity }}
                >
                  {formattedUpdateDate}
                </motion.p>
              )}
            </div>
            <ThemeToggle checked={theme === 'dark'} onChange={() => setTheme((prev) => (prev === 'dark' ? 'light' : 'dark'))} />
          </div>
        </div>
      </header>

      {page === 'dashboard' ? (
        <main className="mx-auto w-full max-w-7xl space-y-6 px-4 py-6 sm:px-6">
          <section id="stats" className="space-y-6">
            <div className="space-y-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">На этой ротации</p>
              <div className="grid gap-4 md:grid-cols-3">
                {kpis.map((item) => (
                  <StatCard key={item.title} {...item} isLoading={isSummaryLoading} />
                ))}
              </div>
            </div>
            <AttendanceChart theme={theme} />
            <ContactDensityChart theme={theme} />
            <FragsDistanceChart theme={theme} />
          </section>

          <section id="heatmaps" className="space-y-6">
            <MapsChart theme={theme} />
          </section>

        </main>
      ) : page === 'playerStats' ? (
        <main className="mx-auto w-full max-w-7xl space-y-6 px-4 py-6 sm:px-6">
          <PlayerStatsTable onSelectPlayer={(nickname) => navigateTo(`/player/${encodeURIComponent(nickname)}`)} />
        </main>
      ) : page === 'playerDetail' && selectedPlayer ? (
        <main className="mx-auto w-full max-w-7xl space-y-6 px-4 py-6 sm:px-6">
          <PlayerDetailPage
            callsign={selectedPlayer}
            theme={theme}
            onBack={() => navigateTo('/player-stats')}
          />
        </main>
      ) : page === 'squadStats' ? (
        <main className="mx-auto w-full max-w-screen-2xl space-y-6 px-4 py-6 sm:px-6">
          <SquadStatsTable onSelectSquad={(name) => navigateTo(`/squad-stats/${encodeURIComponent(name)}`)} />
        </main>
      ) : page === 'squadDetail' && selectedSquad ? (
        <main className="mx-auto w-full max-w-7xl space-y-6 px-4 py-6 sm:px-6">
          <SquadDetailPage
            squad={selectedSquad}
            theme={theme}
            onBack={() => navigateTo('/squad-stats')}
            onNavigatePlayer={(nickname) => navigateTo(`/player/${encodeURIComponent(nickname)}`)}
          />
        </main>
      ) : page === 'sideStats' ? (
        <SideStatsPage
          server={selectedServer}
          theme={theme}
          onSelectServer={(srv) => navigateTo(`/srv_stat/${srv}`)}
        />
      ) : page === 'achievements' ? (
        <AchievementsPage />
      ) : page === 'manualCorrections' ? (
        <ManualCorrectionsPage onNavigate={navigateTo} />
      ) : page === 'manualCorrectionRegistry' ? (
        <ManualCorrectionRegistry />
      ) : page === 'manualCorrectionReceipt' && manualCorrectionId ? (
        <ManualCorrectionReceiptPage publicId={manualCorrectionId} onNavigate={navigateTo} />
      ) : page === 'moderation' ? (
        <ModerationPage
          route={resolveModerationRoute(locationPathname)}
          locationSearch={locationSearch}
          onNavigate={navigateTo}
        />
      ) : page === 'heatmaps' || page === 'archive' ? (
        <main className="mx-auto w-full max-w-7xl space-y-4 px-4 py-10 sm:px-6">
          <div className="card p-6 text-center">
            <p className="text-xl font-semibold">Функционал находится в разработке.</p>
          </div>
        </main>
      ) : page === 'infantry' ? (
        <main className="mx-auto w-full max-w-7xl space-y-6 px-4 py-6 sm:px-6">
          <PlayerStatsTable
            title="Пехота"
            subtitle="Рейтинг бойцов пехоты"
            apiPath="/infantry-stats/"
            queryKey={['infantry-stats']}
            onSelectPlayer={(nickname) => navigateTo(`/player/${encodeURIComponent(nickname)}`)}
          />
        </main>
      ) : page === 'vehicles' ? (
        <main className="mx-auto w-full max-w-7xl space-y-6 px-4 py-6 sm:px-6">
          <PlayerStatsTable
            title="Техника"
            subtitle="Экипажи и пилоты"
            apiPath="/vehicle-stats/"
            queryKey={['vehicle-stats']}
            hiddenColumns={['survival', 'medic']}
            onSelectPlayer={(nickname) => navigateTo(`/player/${encodeURIComponent(nickname)}`)}
          />
        </main>
      ) : page === 'artillery' ? (
        <main className="mx-auto w-full max-w-7xl space-y-6 px-4 py-6 sm:px-6">
          <PlayerStatsTable
            title="Артиллеристы"
            subtitle="Статистика артиллерийских расчётов"
            apiPath="/artillery-stats/"
            queryKey={['artillery-stats']}
            hiddenColumns={['survival', 'medic']}
            helpContext="artillery"
            onSelectPlayer={(nickname) => navigateTo(`/player/${encodeURIComponent(nickname)}`)}
          />
        </main>
      ) : page === 'ks' ? (
        renderTablePage(
          'КС',
          'Статистика командиров серверов',
          ksColumns,
          ksStatsResponse?.items ?? [],
          'player',
          'efficiency',
          {
            efficiency: {
              title: 'КПД',
              description: (
                <div className="space-y-2">
                  <div className="rounded-md border border-border bg-muted/30 px-2 py-1 font-mono text-xs">
                    (Wt × 1.1 + Wl × 0.9) / N
                  </div>
                  <div>
                    <strong>Wl</strong> — Кол-во побед по потерям;
                    <br />
                    <strong>Wt</strong> — Кол-во побед по задаче;
                    <br />
                    <strong>N</strong> — Кол-во игр в роли КСа;
                  </div>
                  <p>
                    <strong>КПД = 0</strong>, если вы отыграли менее 5 игр в роли КСа.
                  </p>
                  <p>
                    <strong>Победа/проигрыш по задачам</strong> — Игра завершилась после выполнения всех задач на
                    миссии;
                    <br />
                    <strong>Победа/проигрыш по потерям</strong> — Игра завершилась при достижении одной из сторон порога
                    потерь.
                  </p>
                </div>
              ),
            },
          }
        )
      ) : page === 'mtsg' ? (
        <main className="mx-auto w-full max-w-7xl space-y-4 px-4 py-6 sm:px-6">
          <div className="rounded-lg border border-border bg-card/60 px-3 py-2 text-sm text-muted-foreground">{mtsgReplayInfo}</div>
          <GenericStatsTable
            title="mTSG"
            subtitle="Расширенная статистика mTSG"
            columns={mtsgColumns}
            rows={mtsgRows}
            linkKey="callsign"
            efficiencyKey="efficiency"
            onLinkClick={(nickname) => navigateTo(`/player/${encodeURIComponent(nickname)}`)}
          />
        </main>
      ) : null}
      <footer className="border-t border-border bg-card/80 py-6">
        <p className="text-center text-foreground/80">
          © 2024 - 2026{' '}
          <a
            href="https://tsgames.ru/user/profile/Krupa"
            className="font-semibold text-foreground hover:text-primary"
            target="_blank"
            rel="noreferrer"
          >
            Krupa
          </a>{' '}
          <a
            href="https://tsgames.ru/forum/topic/podpolnaa-statistika-web"
            className="font-semibold text-foreground hover:text-primary"
            target="_blank"
            rel="noreferrer"
          >
            Обратная связь
          </a>
        </p>
      </footer>
      {showScrollTop ? (
        <button
          type="button"
          onClick={() => window.scrollTo({ top: 0, left: 0, behavior: 'auto' })}
          className="fixed bottom-6 right-6 z-50 inline-flex h-12 w-12 items-center justify-center rounded-full border border-border bg-card text-foreground shadow-lg transition hover:border-primary hover:text-primary"
          aria-label="Наверх"
        >
          <ArrowUp className="h-5 w-5" aria-hidden />
        </button>
      ) : null}
    </div>
  );
}

function ThemeToggle({ checked, onChange }: { checked: boolean; onChange: () => void }): JSX.Element {
  return (
    <div className="flex items-center gap-2">
      <label className="relative inline-flex cursor-pointer items-center">
        <input
          type="checkbox"
          className="peer sr-only"
          checked={checked}
          onChange={onChange}
          aria-label="Переключить тему"
        />
        <span className="h-6 w-14 rounded-full bg-muted transition-colors peer-checked:bg-primary" />
        <span className="absolute left-1 top-1 flex h-4 w-4 items-center justify-center rounded-full bg-foreground shadow transition-transform peer-checked:translate-x-8">
          {checked ? (
            <Sun className="h-3 w-3 text-background" aria-hidden />
          ) : (
            <Moon className="h-3 w-3 text-background" aria-hidden />
          )}
        </span>
      </label>
    </div>
  );
}

function NavigationSparkline({
  theme,
  statsLinks,
  onNavigate,
}: {
  theme: 'light' | 'dark';
  statsLinks: HeaderLink[];
  onNavigate: (path: string) => void;
}): JSX.Element {
  const chartRef = useRef<HTMLDivElement | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const chartInstance = useRef<EChartsType | null>(null);
  const [canRenderChart, setCanRenderChart] = useState(false);
  const [statsMenuOpen, setStatsMenuOpen] = useState(false);
  const [manualMenuOpen, setManualMenuOpen] = useState(false);

  type NavPoint = {
    label: string;
    value: number;
    link: string;
    menu?: 'stats' | 'manualCorrections';
  };
  const navPoints = useMemo(
    () => [
      { label: 'Статистика', value: 48, link: '/#stats', menu: 'stats' as const },
      {
        label: 'Учёт фрагов',
        value: 64,
        link: '/manual-corrections',
        menu: 'manualCorrections' as const,
      },
      { label: 'Достижения', value: 36, link: '/achievements' },
      { label: 'Архив', value: 52, link: '/archive' },
    ],
    []
  );

  useEffect(() => {
    if (!wrapperRef.current || !chartRef.current) return;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      const width = entry?.contentRect.width ?? 0;
      const height = entry?.contentRect.height ?? 0;
      const visible = width > 260 && height > 0 && getComputedStyle(chartRef.current as HTMLElement).display !== 'none';

      setCanRenderChart(visible);

      if (visible) {
        if (!chartInstance.current) {
          chartInstance.current = echarts.init(chartRef.current as HTMLDivElement);
        }
        chartInstance.current.resize();
      } else {
        chartInstance.current?.dispose();
        chartInstance.current = null;
      }
    });

    observer.observe(chartRef.current);

    return () => {
      observer.disconnect();
      chartInstance.current?.dispose();
      chartInstance.current = null;
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(event.target as Node)) {
        setStatsMenuOpen(false);
        setManualMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleNavigation = (item: NavPoint) => {
    if (item.menu === 'stats') {
      setStatsMenuOpen((prev) => !prev);
      setManualMenuOpen(false);
      return;
    }
    if (item.menu === 'manualCorrections') {
      setManualMenuOpen((prev) => !prev);
      setStatsMenuOpen(false);
      return;
    }

    setStatsMenuOpen(false);
    setManualMenuOpen(false);

    if (item.link.startsWith('http')) {
      window.open(item.link, '_blank', 'noreferrer');
      return;
    }

    onNavigate(item.link);
  };

  useEffect(() => {
    if (!canRenderChart || !chartInstance.current) return;

    const styles = getComputedStyle(document.documentElement);
    const raw = (token: string) => styles.getPropertyValue(token).trim();
    const toHsl = (token: string, fallback: string) => {
      const value = raw(token);
      return value ? `hsl(${value})` : fallback;
    };

    const lineColor = toHsl('--primary', '#ffde00');
    const accent = toHsl('--accent', '#ffde00');

    const option = {
      grid: { left: 6, right: 6, top: 10, bottom: 30, containLabel: false },
      xAxis: {
        type: 'category',
        data: navPoints.map((item) => item.label),
        show: false,
        boundaryGap: true,
      },
      yAxis: { type: 'value', show: false },
      tooltip: { show: false, trigger: 'none' },
      series: [
        {
          type: 'line',
          data: navPoints.map((item) => ({
            value: item.value,
            link: item.link,
            name: item.label,
            menu: item.menu,
          })),
          smooth: false,
          symbol: 'circle',
          symbolSize: 10,
          lineStyle: { color: lineColor, width: 3 },
          itemStyle: {
            color: accent,
            borderColor: theme === 'dark' ? toHsl('--card', '#1a1d21') : '#ffffff',
            borderWidth: 2,
          },
          label: {
            show: true,
            position: 'bottom',
            distance: 6,
            formatter: '{b}',
            color: toHsl('--foreground', '#e5e7eb'),
            fontWeight: 600,
            fontSize: 12,
          },
          emphasis: { scale: 1.05 },
          cursor: 'pointer',
          triggerLineEvent: true,
          triggerSymbolEvent: true,
          triggerEvent: true,
        },
      ],
      animationDuration: 0,
      animationEasing: 'linear',
    } as echarts.EChartsOption;

    const handleClick = (params: any) => {
      const point = params?.data as NavPoint | undefined;
      if (!point) return;

      handleNavigation(point);
    };

    chartInstance.current.off('click');
    chartInstance.current.on('click', handleClick);
    chartInstance.current.setOption(option, true);
    chartInstance.current.resize();

    return () => {
      chartInstance.current?.off('click', handleClick);
    };
  }, [navPoints, theme, canRenderChart]);

  return (
    <div ref={wrapperRef} className="relative flex w-full items-center justify-center px-2 sm:px-4">
      <HeaderNavigationMenu
        open={statsMenuOpen}
        sections={[{ links: statsLinks }]}
        onClose={() => setStatsMenuOpen(false)}
        onNavigate={onNavigate}
      />
      <HeaderNavigationMenu
        open={manualMenuOpen}
        sections={[{ links: MANUAL_CORRECTION_LINKS }]}
        onClose={() => setManualMenuOpen(false)}
        onNavigate={onNavigate}
      />
      <div className="hidden w-full flex-wrap items-center justify-center gap-2 lg:hidden" />
      <div className="hidden w-full items-center lg:flex">
        <div ref={chartRef} className="h-16 w-full min-w-[320px]" />
      </div>
    </div>
  );
}

export default App;
